import { useState, useEffect, useRef } from "react";
import { proxyCall } from "../proxy";
import { isValidSlug } from "../slug";
import { parseStored, createSaver, LAST_MODIFIED, buildFinanceData } from "../persist";
import normalizeConfig from "../finance/normalizeConfig";
import validateConfig from "../finance/validateConfig";
import { getDefaultMonthData } from "../finance/calc";
import { standardSteps, firstMonday, sortByDate } from "./steps";

// Everything Thread can change about one engagement, in one place: load,
// parse, the saver (read-before-write, one at a time), and every handler the
// old client dashboard carried. Debounce per field (steps / finance) so an
// edit to each inside the same window saves both. Config saves are explicit.
const todayISO = () => new Date().toLocaleDateString("en-CA");

export default function useEngagement(slug) {
  const [engagement, setEngagement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [steps, setSteps] = useState({});
  const [cfg, setCfg] = useState(() => normalizeConfig(null));
  const [rawConfig, setRawConfig] = useState(null);
  const [configProblems, setConfigProblems] = useState([]);
  const [finance, setFinance] = useState({});
  const [upcoming, setUpcoming] = useState([]);
  const [extras, setExtras] = useState([]);
  const [blockSessions, setBlockSessions] = useState([]);
  const [blockHistory, setBlockHistory] = useState([]);
  const [readOnly, setReadOnly] = useState([]);
  const [stale, setStale] = useState(false);
  const [failed, setFailed] = useState({});
  const saver = useRef(null);
  const stepsTimer = useRef(null);
  const financeTimer = useRef(null);
  // Latest values for the finance payload, so a save built from a stale closure never drops a sibling field.
  const latest = useRef({});
  latest.current = { finance, upcoming, blockSessions, blockHistory, extras, cfg };

  useEffect(() => { load(); }, [slug]);

  async function load() {
    setLoading(true);
    if (!isValidSlug(slug)) { setNotFound(true); setLoading(false); return; }
    try {
      const c = await proxyCall({ action: "engagement", slug });
      if (!c || !c.id) { setNotFound(true); setLoading(false); return; }
      setEngagement(c);
      const stored = parseStored(c.fields);
      setReadOnly(stored.readOnly);
      saver.current = createSaver({ call: proxyCall, recordId: c.id, lastModified: c.fields[LAST_MODIFIED], readOnly: stored.readOnly });
      if (stored.readOnly.includes("Onboarding Steps")) setSteps({});
      // Nothing stored yet: show the standard flow for the type, dated from
      // the start month. It is only written when Thread edits it.
      if (!stored.steps && !stored.readOnly.includes("Onboarding Steps")) {
        const start = /^\d{4}-\d{2}$/.test(stored.config?.engagementStart || "") ? firstMonday(stored.config.engagementStart) : "";
        setSteps(standardSteps(stored.config?.engagementType || c.fields["Engagement Type"], start));
      }
      if (stored.steps) { const sorted = {}; Object.keys(stored.steps).forEach(ph => { sorted[ph] = sortByDate(stored.steps[ph]); }); setSteps(sorted); }
      if (stored.config) { setRawConfig(stored.config); setCfg(normalizeConfig(stored.config)); setConfigProblems(validateConfig(stored.config)); }
      const fd = stored.data;
      if (fd) {
        if (fd.months) setFinance(fd.months);
        if (fd.upcoming) setUpcoming(fd.upcoming);
        if (Array.isArray(fd.extras)) setExtras(fd.extras);
        if (fd.sessionBlock?.sessions) setBlockSessions(fd.sessionBlock.sessions);
        if (fd.sessionBlock?.history) setBlockHistory(fd.sessionBlock.history);
      }
    } catch (e) { console.error(e); setNotFound(true); }
    setLoading(false);
  }

  const persist = async (fields) => {
    if (!saver.current) return "noSaver";
    setFailed(prev => { const u = { ...prev }; Object.keys(fields).forEach(k => delete u[k]); return u; });
    try {
      const r = await saver.current.save(fields);
      if (r === "stale") setStale(true);
      return r;
    } catch (e) { console.error(e); setFailed(prev => ({ ...prev, ...fields })); return "failed"; }
  };

  // ── steps ──
  const saveSteps = (s) => persist({ "Onboarding Steps": JSON.stringify(s) });
  const debouncedSaveSteps = (s) => { if (stepsTimer.current) clearTimeout(stepsTimer.current); stepsTimer.current = setTimeout(() => saveSteps(s), 500); };
  const mutateSteps = (fn) => setSteps(prev => { const u = fn(prev); debouncedSaveSteps(u); return u; });
  const stepActions = {
    setStatus: (phase, id, status) => mutateSteps(p => ({ ...p, [phase]: p[phase].map(s => s.id === id ? { ...s, status } : s) })),
    setDate: (phase, id, date) => mutateSteps(p => ({ ...p, [phase]: sortByDate(p[phase].map(s => s.id === id ? { ...s, date } : s)) })),
    // patch = { date, time?, endTime?, tz? }; an undefined key is removed, so
    // clearing the time leaves a plain date-only step (src/time.js).
    updateWhen: (phase, id, patch) => mutateSteps(p => ({ ...p, [phase]: sortByDate(p[phase].map(s => {
      if (s.id !== id) return s;
      const n = { ...s, ...patch }; ["time", "endTime", "tz", "start"].forEach(k => { if (n[k] === undefined) delete n[k]; }); return n;
    })) })),
    add: (phase, label, description) => mutateSteps(p => ({ ...p, [phase]: sortByDate([...(p[phase] || []), { id: `${phase.substring(0, 2).toLowerCase()}${Date.now()}`, label, description, status: "to-start", date: "" }]) })),
    remove: (phase, id) => mutateSteps(p => ({ ...p, [phase]: p[phase].filter(s => s.id !== id) })),
    // patch = { owner?, internal? }; a null / false value removes the key so
    // the stored step stays as small as it was.
    setMeta: (phase, id, patch) => mutateSteps(p => ({ ...p, [phase]: p[phase].map(s => {
      if (s.id !== id) return s;
      const n = { ...s, ...patch }; ["owner", "internal"].forEach(k => { if (!n[k]) delete n[k]; }); return n;
    }) })),
    edit: (phase, id, label, description) => mutateSteps(p => ({ ...p, [phase]: p[phase].map(s => s.id === id ? { ...s, label, description } : s) })),
  };

  // ── finance data ──
  const financePayload = (over = {}) => {
    const l = { ...latest.current, ...over };
    return buildFinanceData({ hasSessionBlock: !!l.cfg.modules.sessionBlock, months: l.finance, upcoming: l.upcoming, sessions: l.blockSessions, history: l.blockHistory, extras: l.extras });
  };
  const saveFinance = (over) => persist({ "Finance Data": JSON.stringify(financePayload(over)) });
  const debouncedSaveFinance = (over) => {
    latest.current = { ...latest.current, ...over };
    if (financeTimer.current) clearTimeout(financeTimer.current);
    financeTimer.current = setTimeout(() => saveFinance(), 500);
  };
  const month = (prev, m) => prev[m] || getDefaultMonthData(latest.current.cfg, m);
  const mutateFinance = (fn) => setFinance(prev => { const u = fn(prev); debouncedSaveFinance({ finance: u }); return u; });
  const setUpcomingSaved = (u) => { setUpcoming(u); debouncedSaveFinance({ upcoming: u }); };
  const setBlockSaved = (fn) => setBlockSessions(prev => { const u = fn(prev); debouncedSaveFinance({ blockSessions: u }); return u; });
  const setExtrasSaved = (u) => { setExtras(u); debouncedSaveFinance({ extras: u }); };

  const financeActions = {
    toggleCoaching: (m, id) => mutateFinance(p => { const md = month(p, m); return { ...p, [m]: { ...md, coaching: md.coaching.map(c => c.id === id ? { ...c, happened: !c.happened } : c) } }; }),
    setCoachingDate: (m, id, date) => mutateFinance(p => { const md = month(p, m); return { ...p, [m]: { ...md, coaching: md.coaching.map(c => c.id === id ? { ...c, date } : c) } }; }),
    addCoachingSession: (m, label, date) => mutateFinance(p => { const md = month(p, m); return { ...p, [m]: { ...md, coaching: [...md.coaching, { id: `oo${Date.now()}`, label, happened: true, oneOff: true, ...(date ? { date } : {}) }] } }; }),
    deleteCoachingSession: (m, id) => mutateFinance(p => ({ ...p, [m]: { ...p[m], coaching: p[m].coaching.filter(c => c.id !== id) } })),
    addLineItem: (m, item) => mutateFinance(p => { const md = month(p, m); return { ...p, [m]: { ...md, lineItems: [...md.lineItems, { id: `li${Date.now()}`, ...item }] } }; }),
    deleteLineItem: (m, id) => mutateFinance(p => ({ ...p, [m]: { ...p[m], lineItems: p[m].lineItems.filter(li => li.id !== id) } })),
    updateLineItem: (m, id, patch) => mutateFinance(p => { const md = month(p, m); return { ...p, [m]: { ...md, lineItems: md.lineItems.map(li => { if (li.id !== id) return li; const n = { ...li, ...patch, rateLabel: "rateLabel" in patch ? patch.rateLabel : (patch.amount !== undefined && patch.amount !== li.amount ? "Bespoke" : li.rateLabel) }; Object.keys(n).forEach(k => { if (n[k] === undefined || (k === "billedSeparately" && !n[k])) delete n[k]; }); return n; }) } }; }),
    moveLineItem: (m, id, dir) => mutateFinance(p => {
      const md = p[m]; if (!md) return p;
      const items = md.lineItems; const i = items.findIndex(x => x.id === id); if (i < 0) return p;
      const kind = x => !!x.billedSeparately; let j = i + dir;
      while (j >= 0 && j < items.length && kind(items[j]) !== kind(items[i])) j += dir;
      if (j < 0 || j >= items.length) return p;
      const next = [...items]; [next[i], next[j]] = [next[j], next[i]];
      return { ...p, [m]: { ...md, lineItems: next } };
    }),
    updateUpcomingItem: (id, patch) => setUpcomingSaved(latest.current.upcoming.map(x => x.id === id ? { ...x, ...patch } : x)),
    addUpcomingItem: (label, amount) => setUpcomingSaved([...latest.current.upcoming, { id: `up${Date.now()}`, label, amount }]),
    deleteUpcomingItem: (id) => setUpcomingSaved(latest.current.upcoming.filter(x => x.id !== id)),
    setBlockSessionStatus: (id, status) => setBlockSaved(prev => prev.map(s => s.id === id ? { ...s, status: s.status === status ? "scheduled" : status } : s)),
    setBlockSessionDate: (id, date) => setBlockSaved(prev => prev.map(s => s.id === id ? { ...s, date } : s)),
    addBlockSession: (date) => setBlockSaved(prev => { const blockNo = latest.current.cfg.modules.sessionBlock?.blockNo; return [...prev, { id: `bs${Date.now()}`, date: date || "", status: "scheduled", ...(blockNo ? { block: blockNo } : {}) }]; }),
    deleteBlockSession: (id) => setBlockSaved(prev => prev.filter(s => s.id !== id)),
    addExtra: ({ label, amount, date }) => setExtrasSaved([...latest.current.extras, { id: `ex${Date.now()}`, label, amount, ...(date ? { date } : {}), status: "to-invoice" }]),
    updateExtra: (id, patch) => setExtrasSaved(latest.current.extras.map(x => x.id === id ? { ...x, ...patch } : x)),
    deleteExtra: (id) => setExtrasSaved(latest.current.extras.filter(x => x.id !== id)),
  };

  // ── config (explicit save) ──
  // `extraFields`: Status / Engagement Type / Logo URL alongside. `data` (optional) is a new
  // Finance Data block payload saved in the same update (Start new block).
  const saveConfig = async (nativeCfg, extraFields = {}, data) => {
    const fields = { "Finance Config": JSON.stringify(nativeCfg), ...extraFields };
    if (data) {
      fields["Finance Data"] = JSON.stringify(financePayload({ cfg: normalizeConfig(nativeCfg), blockSessions: data.sessions, blockHistory: data.history }));
    }
    const r = await persist(fields);
    if (r === "saved") {
      setRawConfig(nativeCfg); setCfg(normalizeConfig(nativeCfg)); setConfigProblems(validateConfig(nativeCfg));
      if (data) { setBlockSessions(data.sessions); setBlockHistory(data.history); }
      setEngagement(e => e ? { ...e, fields: { ...e.fields, ...extraFields } } : e);
    }
    return r;
  };

  return {
    engagement, loading, notFound, readOnly, stale, failed, configProblems,
    steps, cfg, rawConfig, finance, upcoming, extras, blockSessions, blockHistory,
    stepActions, financeActions, saveConfig, retry: () => persist(failed), today: todayISO(),
  };
}
